import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, ExternalLink } from "lucide-react";
import { api, storeGoUrl } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useSite } from "@/lib/site";
import { storeLd } from "@/lib/structuredData";
import { DealGrid, DealGridSkeleton } from "@/components/site/DealCard";
import { Crumbs, Empty } from "@/components/site/bits";
import { StoreCoupons } from "@/components/site/StoreCoupons";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS = [["score", "Best score"], ["discount", "Biggest discount"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["newest", "Newest"]];

const Filters = ({ params, set, cats, merchants, fixed }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="deal-filters">
    <SlidersHorizontal size={18} className="text-slate-400 hidden sm:block mb-2.5" />
    {fixed !== "category" && (
      <Select value={params.get("category") || "all"} onValueChange={(v) => set("category", v === "all" ? "" : v)}>
        <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="all">All categories</SelectItem>
          {cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.parent_id ? "— " : ""}{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
    {fixed !== "merchant" && (
      <Select value={params.get("merchant") || "all"} onValueChange={(v) => set("merchant", v === "all" ? "" : v)}>
        <SelectTrigger className="w-40" data-testid="filter-merchant"><SelectValue placeholder="Store" /></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="all">All stores</SelectItem>
          {merchants.map((m) => <SelectItem key={m.id} value={m.slug}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
    <Select value={params.get("min_discount") || "0"} onValueChange={(v) => set("min_discount", v === "0" ? "" : v)}>
      <SelectTrigger className="w-36" data-testid="filter-discount"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        {["0", "20", "30", "50", "70"].map((v) => <SelectItem key={v} value={v}>{v === "0" ? "Any discount" : `${v}%+ off`}</SelectItem>)}
      </SelectContent>
    </Select>
    <input type="number" min="0" placeholder="Max ₹" defaultValue={params.get("max_price") || ""} data-testid="filter-max-price"
      onBlur={(e) => set("max_price", e.target.value)} onKeyDown={(e) => e.key === "Enter" && set("max_price", e.currentTarget.value)} className="h-10 w-28 rounded-md border border-slate-200 px-3 text-sm" />
    <div className="ml-auto">
      <Select value={params.get("sort") || "score"} onValueChange={(v) => set("sort", v)}>
        <SelectTrigger className="w-48" data-testid="sort-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </div>
);

export default function Deals({ mode }) {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const set = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    if (k !== "page") p.delete("page");
    setParams(p);
  };
  const query = new URLSearchParams(params);
  if (mode === "category") query.set("category", slug);
  if (mode === "merchant") query.set("merchant", slug);
  const cats = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/public/categories").then((r) => r.data) });
  const merchants = useQuery({ queryKey: ["merchants"], queryFn: () => api.get("/public/merchants").then((r) => r.data) });
  const info = useQuery({
    queryKey: [mode, slug], enabled: !!mode,
    queryFn: () => api.get(mode === "category" ? `/public/categories/${slug}` : `/public/merchants/${slug}`).then((r) => r.data),
  });
  const { data, isLoading } = useQuery({ queryKey: ["deals", query.toString()], queryFn: () => api.get(`/public/deals?${query}`).then((r) => r.data) });
  const q = params.get("q");
  const couponOnly = mode === "merchant" && data && data.total === 0 && params.toString() === "" && info.data?.coupons?.length > 0;
  const title = info.data ? (mode === "merchant" ? `${info.data.name} deals & coupons` : `${info.data.name} deals`) : q ? `Results for “${q}”` : "All deals";
  const faqLd = mode === "category" && info.data?.faqs?.length ? { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: info.data.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : null;
  const siteName = useSite().name;
  useSeo({ title, description: info.data?.meta_description || info.data?.description || `Browse ${title.toLowerCase()} with verified prices in INR on ${siteName}.`, noindex: !!q, jsonLd: mode === "merchant" && info.data ? storeLd(info.data, data?.items || []) : faqLd });
  const page = Number(params.get("page") || 1);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="deals-page">
      <Crumbs items={[["/", "Home"], mode === "category" ? ["/categories", "Categories"] : mode === "merchant" ? ["/stores", "Stores"] : ["/deals", "Deals"], ...(mode ? [[null, info.data?.name || "…"]] : [])]} />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="deals-page-title">{title}</h1>
      {info.data?.description && <p className="text-slate-500 mt-2 max-w-2xl">{info.data.description}</p>}
      {mode === "merchant" && info.data?.has_store_link && (
        <a href={storeGoUrl(info.data.slug)} target="_blank" rel="nofollow sponsored noopener" data-testid="visit-store-button"
          className="inline-flex items-center gap-2 mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-full transition-colors">
          Visit {info.data.name} <ExternalLink size={16} />
        </a>
      )}
      {mode === "category" && info.data?.children?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {info.data.children.map((c) => <a key={c.id} href={`/category/${c.slug}`} className="text-sm px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 font-semibold">{c.name}</a>)}
        </div>
      )}
      {mode === "merchant" && info.data && <StoreCoupons store={info.data} />}
      {mode === "merchant" && !couponOnly && info.data?.coupons?.length > 0 && <h2 className="text-2xl font-bold mt-12">{info.data.name} product deals</h2>}
      {!couponOnly && <>
        <div className="mt-8 mb-6"><Filters params={params} set={set} cats={cats.data || []} merchants={merchants.data || []} fixed={mode} /></div>
        <p className="text-sm text-slate-500 mb-4" data-testid="deals-result-count">{data ? `${data.total} deals found` : " "}</p>
        {isLoading ? <DealGridSkeleton /> : data.items.length ? <DealGrid deals={data.items} /> : <Empty text="No deals match these filters yet." />}
      </>}
      {mode === "category" && info.data?.faqs?.length > 0 && (
        <section className="mt-14 max-w-3xl" data-testid="category-faqs">
          <h2 className="text-2xl font-bold mb-4">Frequently asked questions</h2>
          {info.data.faqs.map((f) => (
            <details key={f.q} className="border-b border-slate-200 py-3"><summary className="font-semibold cursor-pointer">{f.q}</summary><p className="text-slate-600 mt-2 text-sm">{f.a}</p></details>
          ))}
        </section>
      )}
      {data?.pages > 1 && (
        <div className="flex justify-center gap-2 mt-10" data-testid="pagination">
          {Array.from({ length: data.pages }).map((_, i) => (
            <button key={i} onClick={() => set("page", String(i + 1))} data-testid={`page-${i + 1}`}
              className={`h-10 w-10 rounded-full font-bold ${page === i + 1 ? "bg-emerald-600 text-white" : "bg-white border border-slate-200"}`}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
